import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useRef, useState } from 'react';

export type ScribeRoomStatus = 'pending' | 'ongoing' | 'finished';

export type ScribeRoomCardData = {
  id: string;
  hostAvaId: string;
  hostAvaName?: string;
  hostUserName?: string;
  requestStartTs: number;
  status: ScribeRoomStatus;
  subType?: string;
  remainingCreditMinutes?: number;
  conversationMode: string;
  speechLanguage?: string;
  translationLanguage?: string;
  boostedWords?: string[];
  activeScribeNames?: string[];
  osInfo?: string;
  avaAppVersion?: string;
  participants?: string[];
};

type Props = {
  room: ScribeRoomCardData;
  nowTs: number;
  onEnter: (roomId: string) => void;
  onWatch: (roomId: string) => void;
};

const STATUS_CONFIG: Record<ScribeRoomStatus, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pending', color: '#D32F2F', bg: '#FFEBEE' },
  ongoing: { label: 'Ongoing', color: '#2E7D32', bg: '#E8F5E9' },
  finished: { label: 'Finished', color: '#F57F17', bg: '#FFFDE7' },
};

function formatWaitTime(elapsedMs: number): string {
  const totalSeconds = Math.floor(elapsedMs / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

export const ScribeRoomCard: React.FC<Props> = ({ room, nowTs, onEnter, onWatch }) => {
  const statusCfg = STATUS_CONFIG[room.status];
  const elapsedMs = Math.max(0, nowTs - room.requestStartTs);
  const requestTime = new Date(room.requestStartTs).toLocaleTimeString();

  const [boostedAnchor, setBoostedAnchor] = useState<HTMLElement | null>(null);
  const [infoAnchor, setInfoAnchor] = useState<HTMLElement | null>(null);
  const boostedBtnRef = useRef<HTMLDivElement>(null);
  const infoBtnRef = useRef<HTMLButtonElement>(null);

  const isPrecise = room.conversationMode.toLowerCase() === 'precise';
  const hasTranslation = Boolean(room.translationLanguage);
  const boostedCount = room.boostedWords?.length ?? 0;

  return (
    <Card
      variant="outlined"
      sx={{
        borderLeft: `4px solid ${statusCfg.color}`,
        mb: 1.5,
      }}
    >
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
              {room.hostAvaName ?? room.hostAvaId}
            </Typography>
            {room.hostUserName && (
              <Typography variant="body2" color="text.secondary">
                {room.hostUserName}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={statusCfg.label}
              size="small"
              sx={{
                backgroundColor: statusCfg.bg,
                color: statusCfg.color,
                fontWeight: 700,
                fontSize: 12,
              }}
            />
            <Tooltip title="Room info">
              <IconButton
                ref={infoBtnRef}
                size="small"
                onClick={() => setInfoAnchor(infoAnchor ? null : infoBtnRef.current)}
                aria-label="Room info"
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Started {requestTime} &mdash; waiting {formatWaitTime(elapsedMs)}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
          {room.subType && <Chip label={room.subType} size="small" variant="outlined" />}
          {room.remainingCreditMinutes != null && (
            <Chip label={`Credit: ${room.remainingCreditMinutes}min`} size="small" variant="outlined" />
          )}
          {room.speechLanguage && <Chip label={room.speechLanguage} size="small" variant="outlined" />}
          <Chip
            label={hasTranslation ? `Translated to ${room.translationLanguage}` : 'Translation off'}
            size="small"
            variant={hasTranslation ? 'filled' : 'outlined'}
            color={hasTranslation ? 'primary' : 'default'}
            sx={!hasTranslation ? { color: 'text.disabled' } : undefined}
          />
          <Chip
            label={isPrecise ? 'Precise Mode' : 'Fast Mode'}
            size="small"
            variant={isPrecise ? 'filled' : 'outlined'}
            color={isPrecise ? 'primary' : 'default'}
            sx={!isPrecise ? { color: 'text.disabled' } : undefined}
          />
          <Chip
            ref={boostedBtnRef}
            label={`Boosted Words (${boostedCount})`}
            size="small"
            variant="outlined"
            clickable={boostedCount > 0}
            onClick={
              boostedCount > 0 ? () => setBoostedAnchor(boostedAnchor ? null : boostedBtnRef.current) : undefined
            }
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="contained" onClick={() => onEnter(room.id)}>
            Enter
          </Button>
          <Button size="small" variant="outlined" onClick={() => onWatch(room.id)}>
            Watch
          </Button>
        </Box>
      </CardContent>

      <Popover
        open={Boolean(boostedAnchor)}
        anchorEl={boostedAnchor}
        onClose={() => setBoostedAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 160 } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Boosted Words
        </Typography>
        {boostedCount === 0 ? (
          <Typography variant="body2" color="text.secondary">
            None
          </Typography>
        ) : (
          room.boostedWords?.map((word) => (
            <Typography key={word} variant="body2">
              {word}
            </Typography>
          ))
        )}
      </Popover>

      <Popover
        open={Boolean(infoAnchor)}
        anchorEl={infoAnchor}
        onClose={() => setInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 2, minWidth: 240 } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Room Info
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Room ID: {room.id}
        </Typography>
        {room.osInfo && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            OS: {room.osInfo}
          </Typography>
        )}
        {room.avaAppVersion && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Ava version: {room.avaAppVersion}
          </Typography>
        )}
        {room.participants && room.participants.length > 0 && (
          <>
            <Typography variant="body2" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>
              Participants
            </Typography>
            {room.participants.map((p) => (
              <Typography key={p} variant="body2" color="text.secondary">
                {p}
              </Typography>
            ))}
          </>
        )}
        {room.activeScribeNames && room.activeScribeNames.length > 0 && (
          <>
            <Typography variant="body2" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>
              Active Scribes
            </Typography>
            {room.activeScribeNames.map((s) => (
              <Typography key={s} variant="body2" color="text.secondary">
                {s}
              </Typography>
            ))}
          </>
        )}
        <Button size="small" variant="outlined" sx={{ mt: 1.5 }} disabled>
          Copy Logs
        </Button>
      </Popover>
    </Card>
  );
};
